import { useEffect, useState } from "react";
import Lenis from "lenis";
import { Navbar } from "@/components/site/Navbar";
import { Hero } from "@/components/site/Hero";
import { EditorialMarquee } from "@/components/site/Marquee";
import { Angebote } from "@/components/site/Angebote";
import { Sorglos } from "@/components/site/Sorglos";
import { Preise } from "@/components/site/Preise";
import { Verfuegbarkeit } from "@/components/site/Verfuegbarkeit";
import { Galerie } from "@/components/site/Galerie";
import { Bewertungen, BewertungForm } from "@/components/site/Bewertungen";
import { Kontakt } from "@/components/site/Kontakt";
import { Footer } from "@/components/site/Footer";
import { ShareFab } from "@/components/site/ShareFab";
import { Logo } from "@/components/site/Logo";
import { api } from "@/lib/api";
import { NOISE_BG, PHONE, PHONE_HREF } from "@/lib/site";
import { Phone, Wrench } from "lucide-react";

const MaintenanceScreen = ({ message }) => (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#120E09] px-6 text-center" data-testid="maintenance-screen">
        <Logo size={96} />
        <div className="mt-10 flex items-center gap-3 text-[#C88A45]">
            <Wrench size={22} />
            <p className="text-xs font-semibold uppercase tracking-[0.25em]">Wartungsarbeiten</p>
        </div>
        <h1 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-[#EDE6DA] sm:text-5xl">
            Gleich sind wir <span className="text-[#C88A45]">zurück.</span>
        </h1>
        <p className="mt-6 max-w-md text-sm leading-relaxed text-[#9C917D]">{message}</p>
        <a
            data-testid="maintenance-phone"
            href={PHONE_HREF}
            className="mt-10 flex items-center gap-2 rounded-full bg-[#C88A45] px-7 py-3 text-sm font-bold uppercase tracking-wide text-[#120E09] transition-colors hover:bg-[#DBA057]"
        >
            <Phone size={15} /> {PHONE}
        </a>
    </div>
);

export default function Landing() {
    const [maintenance, setMaintenance] = useState(null);

    useEffect(() => {
        api.get("/settings/public")
            .then((r) => setMaintenance(r.data))
            .catch(() => setMaintenance({ maintenance: false, message: "" }));
    }, []);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 1.1 });
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    if (maintenance === null) {
        return <div className="flex min-h-screen items-center justify-center bg-[#120E09] text-sm text-[#9C917D]">Lade …</div>;
    }
    if (maintenance.maintenance) {
        return <MaintenanceScreen message={maintenance.message} />;
    }

    return (
        <div className="bg-[#120E09]" data-testid="landing-page">
            <div
                className="pointer-events-none fixed inset-0 z-[60] opacity-[0.05] mix-blend-overlay"
                style={{ backgroundImage: NOISE_BG }}
                aria-hidden="true"
            />
            <Navbar />
            <main>
                <Hero />
                <EditorialMarquee />
                <Angebote />
                <Sorglos />
                <Preise />
                <Verfuegbarkeit />
                <Galerie />
                <Kontakt />
            </main>
            <Bewertungen />
            <BewertungForm />
            <Footer />
            <ShareFab />
            <a
                data-testid="whatsapp-button"
                href="https://wa.me/4915120707720?text=Hallo%20KischioWood%2C%20ich%20interessiere%20mich%20f%C3%BCr%20Ihr%20Holz."
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Per WhatsApp anfragen"
                className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-[0_4px_16px_rgba(0,0,0,0.4)] transition-transform duration-200 hover:scale-110 active:scale-95"
            >
                <svg viewBox="0 0 24 24" width="28" height="28" fill="#120E09" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
            </a>
        </div>
    );
}
